import StageQr from "@/components/StageQr";
import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Link } from "react-router-dom";
import { useLiveRefresh } from "@/lib/live";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { printElement } from "@/lib/print";
import { SheetBody } from "@/components/stores/StoresSheet";
import PrintingSheet, { totalsPatch } from "@/components/PrintingSheet";
import AvtSheet, { AVT_SHEET_FIELDS, avtTotalsPatch } from "@/components/AvtSheet";
import SlittingSheet, { SLIT_SHEET_FIELDS, slitTotalsPatch } from "@/components/SlittingSheet";
import COAEditor from "@/components/COAEditor";
import QualitySheet, { qualityTotalsPatch } from "@/components/QualitySheet";
import PackingSheet, { PACK_SHEET_FIELDS, packTotalsPatch } from "@/components/PackingSheet";
import DispatchLabels from "@/components/DispatchLabels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Lock, Package, Printer, Scissors, ClipboardCheck, ShieldCheck, Award, Truck, Layers, ArrowRightCircle } from "lucide-react";
import DatePicker from "@/components/DatePicker";
import TimePicker from "@/components/TimePicker";
import MilestonesTimeline from "@/components/MilestonesTimeline";

// Human labels for the read-only common/carried context box
const NICE = {
  mpwo_number: "MPWO",
  bpr_number: "BPR No.",
  customer_name: "Customer",
  product_name: "Product",
  product_code: "Product Code",
  quantity: "Order Qty",
  substrate_type: "Substrate",
  paper_type: "Paper Type",
  paper_code: "Paper Code",
  paper_supplier: "Supplier",
  paper_width_mm: "Paper Width (mm)",
  grn_number: "GRN No.",
  lot_id: "Lot ID",
  qc_ar_number: "QC AR No.",
  issued_qty_meters: "Issued Qty (m)",
  qty_forward_to_avt_labels: "Qty Fwd to AVT",
  no_of_printed_rolls: "Printed Rolls",
  qty_forward_to_slitting: "Qty Fwd to Slitting",
  qty_forward_to_packing: "Qty Fwd to Packing",
  total_no_of_rolls: "Total Rolls",
  finished_qty: "Finished Qty",
  box_no: "Box No.",
  coa_number: "COA No.",
  batch_no: "Batch No.",
  label_length_mm: "Label Length (mm)",
  label_width_mm: "Label Width (mm)",
  directions: "Directions",
  label_size: "Label Size",
  die_no: "Die No.",
  lo_li_ticks: "Winding",
  die_size: "Die Size",
  die_code: "Die Code",
  no_of_colors: "No. of Colours",
  colour_details: "Colour Details",
  substrate_code: "Substrate Code",
  ups_across: "Ups Across",
  ups_along: "Ups Along",
  total_ups_per_repeat: "Ups per Repeat",
  cylinder_mm: "Cylinder (mm)",
  direction: "Direction",
  core_diameter: "Core Diameter",
  gsm: "GSM",
  assigned_machine_code: "Assigned Machine",
};

const STAGE_ICONS = {
  stores_issue: Package,
  printing: Printer,
  flatbed: Layers,
  avt: ClipboardCheck,
  slitting: Scissors,
  quality: ShieldCheck,
  packing: Package,
  coa: ShieldCheck,
  dispatch: Truck,
};

// Field labels for the stage form. Anything missing falls back to a title-cased key.
const FIELD_LABEL = {
  paper_type: "Paper Type", paper_code: "Paper Code", paper_supplier: "Paper Supplier",
  paper_width_mm: "Paper Width (mm)", grn_number: "GRN No.", lot_id: "Lot ID",
  qc_ar_number: "QC AR No.", issued_qty_meters: "Issued Qty (meters)",
  issued_rolls: "No. of Rolls Issued", issued_by: "Issued By",
  date: "Date", operator_name: "Operator Name", assistant_name: "Assistant Name",
  start_time: "Start Time", end_time: "End Time", shift: "Shift", box_no: "Box No.",
  job_type: "Job Type (New/Repeat)", directions: "Directions (Clockwise/Anti-CW)",
  ups_across: "Ups Across", ups_around: "Ups Around", no_of_ups: "No. of Ups",
  label_size: "Label Size", repeat_length_mm: "Repeat Length (mm)", die_no: "Die No.",
  issued_a_meters: "Issued Qty A (m)", make_ready_wastage_b: "Make-Ready Wastage B (m)",
  total_counter_c: "Total Counter C (m)", partial_counter_d: "Partial Counter D (m)",
  in_process_wastage_e: "In-Process Wastage E = C-D (auto)",
  total_consumption_f: "Total Consumption F = B+C (auto)",
  qty_returned_to_stores: "Qty Returned to Stores = A-F (auto)",
  no_of_printed_rolls: "No. of Printed Rolls",
  qty_forward_to_avt_labels: "Qty Forward to AVT (labels)",
  die_station: "Die Station", remarks: "Remarks",
  checked_by_qc: "Checked By (QC)", approved_by_supervisor: "Approved By (Supervisor)",
  sheets_input: "Sheets Input", sheets_ok: "Sheets OK", sheets_wastage: "Sheets Wastage",
  qc_sign: "QC Sign",
  qty_received_from_printing: "Qty Received from Printing",
  qty_inspected: "Qty Inspected", qty_forward_to_slitting: "Qty Forward to Slitting",
  wastage_qty: "Wastage Qty",
  qty_received_from_avt: "Qty Received from AVT", qty_numbering: "Qty Numbering",
  qty_forward_to_packing: "Qty Forward to Packing", qty_wastage: "Qty Wastage",
  qty_destruction: "Qty Destruction",
  rolls_breakdown: "Rolls Breakdown (Roll Qty × No. of Rolls, one per line)",
  total_qty: "Total Qty", total_no_of_rolls: "Total No. of Rolls",
  ups: "UPS", width_mm: "Width (mm)", rolls_details: "Rolls Details",
  meter_details: "Meter Details", labels_details: "Labels Details",
  weight_kg: "Weight (kg)", finished_qty: "Finished Qty",
  packed_by: "Packed By",
  coa_number: "COA Number", issue_date: "Issue Date", batch_no: "Batch No.",
  mfg_date: "Mfg Date", expiry_date: "Expiry Date",
  shade_card_ref: "Shade Card Ref", avt_report_ref: "AVT Report Ref",
  print_registration_ok: "Print Registration OK",
  colour_match_ok: "Colour Match OK", die_cut_ok: "Die Cut OK",
  labels_per_roll_ok: "Labels/Roll OK", packing_ok: "Packing OK",
  prepared_by_qc: "Prepared By (QC)", approved_by_qa: "Approved By (QA)",
  dispatch_date: "Dispatch Date", vehicle_no: "Vehicle No.",
  invoice_number: "Invoice Number", invoice_date: "Invoice Date",
  dc_number: "DC Number", gp_number: "GP Number",
  receive_qty: "Receive Qty", dispatch_qty: "Dispatch Qty",
  prepared_by: "Prepared By",
};

// Fields that render as date pickers or textareas
const DATE_FIELDS = new Set(["date", "issue_date", "mfg_date", "expiry_date", "invoice_date", "dispatch_date"]);
const TIME_FIELDS = new Set(["start_time", "end_time"]);
const AREA_FIELDS = new Set(["remarks", "rolls_breakdown",
                             "rolls_details", "meter_details", "labels_details"]);
const BOOL_PREFIXES = ["chk_", "print_registration_ok", "colour_match_ok", "die_cut_ok",
                       "labels_per_roll_ok", "packing_ok"];
const isBoolField = (k) => BOOL_PREFIXES.some((p) => k === p || k.startsWith(p));

// Fields the client computes from other fields
const COMPUTED = new Set(["in_process_wastage_e", "total_consumption_f", "qty_returned_to_stores", "wastage_qty"]);

const humanize = (k) => FIELD_LABEL[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Wastage thresholds per stage — computed as wastage / (input reference qty) * 100
// If a stage's computed % goes above `warn`, show amber; above `alert` -> red.
const WASTE_THRESHOLDS = {
  printing:  { warn: 3, alert: 6, num: (d) => parseFloat(d.total_consumption_f) - parseFloat(d.qty_forward_to_avt_labels || d.total_counter_c), den: (d) => parseFloat(d.total_consumption_f) },
  flatbed:   { warn: 2, alert: 5, num: (d) => parseFloat(d.sheets_wastage), den: (d) => parseFloat(d.sheets_input) },
  avt:       { warn: 2, alert: 5, num: (d) => parseFloat(d.wastage_qty), den: (d) => parseFloat(d.qty_received_from_printing) },
  slitting:  { warn: 2, alert: 5, num: (d) => (parseFloat(d.qty_wastage) || 0) + (parseFloat(d.qty_destruction) || 0), den: (d) => parseFloat(d.qty_received_from_avt) },
};

function computeWastePct(stageKey, data) {
  const cfg = WASTE_THRESHOLDS[stageKey];
  if (!cfg) return null;
  const num = cfg.num(data || {});
  const den = cfg.den(data || {});
  if (!Number.isFinite(num) || !Number.isFinite(den) || den <= 0) return null;
  return { pct: (num / den) * 100, warn: cfg.warn, alert: cfg.alert };
}

// Small chip renderer used inside StageCard header
function WasteChip({ stageKey, data }) {
  const r = computeWastePct(stageKey, data);
  if (!r) return null;
  const tone = r.pct >= r.alert ? "bg-red-100 text-red-700 border-red-200"
             : r.pct >= r.warn  ? "bg-amber-100 text-amber-700 border-amber-200"
             : "bg-emerald-100 text-emerald-700 border-emerald-200";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-sm border ${tone}`}
      data-testid={`stage-${stageKey}-waste-chip`}
      title={`Waste ${r.pct.toFixed(1)}% (warn ≥ ${r.warn}%, alert ≥ ${r.alert}%)`}
    >
      Waste {r.pct.toFixed(1)}%
    </span>
  );
}

// Mirrors routes/job_status.py ROLE_STAGES — which stage(s) each department role owns. The server is
// what actually enforces this for a real login of one of these roles (routes/jobs.py, routes/
// production_flow.py); this copy exists only so admin's "View as" preview (display-only — the server
// still answers with the admin's full access) can show the SAME cut-down Production Flow a real login
// of that role would see, without needing a second account to check.
const DEPT_STAGE_OWNERSHIP = {
  stores_keeper: ["stores_issue"],
  printing_operator: ["printing"],
  avt_operator: ["avt"],
  slitting_operator: ["slitting"],
  qc_inspector: ["quality", "coa"],
  packing_operator: ["packing"],
  dispatch_clerk: ["dispatch"],
};

export default function ProductionFlow({ jobId, initialStageKey = null }) {
  const { user, previewing } = useAuth();
  const [stages, setStages] = useState([]);
  const [job, setJob] = useState(null);
  const [jobDone, setJobDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(initialStageKey);
  const cards = useRef({});
  // "Print all sheets": every sheet of the job is mounted off screen, printed one per page, then the job's QR last.
  const [printAll, setPrintAll] = useState(false);
  const allRef = useRef(null);
  // Only while previewing (admin "View as"): a real login of this role never reaches this branch,
  // since the server itself already only ever sends back the stage(s) it owns.
  const ownStages = previewing ? DEPT_STAGE_OWNERSHIP[user?.role] : null;

  // `quiet` refreshes in place (after a save, or a change by someone else) so open
  // stage forms stay mounted and keep what is being typed.
  const load = (quiet = false) => {
    if (!quiet) setLoading(true);
    return Promise.all([
      api.get(`/jobs/${jobId}`),
      api.get(`/jobs/${jobId}/stages`),
    ])
      .then(([jobRes, stagesRes]) => {
        setJob(jobRes.data);
        const got = stagesRes.data.stages || [];
        setStages(ownStages ? got.filter((s) => ownStages.includes(s.key)) : got);
        setJobDone(!!stagesRes.data.job_done);
      })
      .catch((e) => !quiet && toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (jobId) load(); /* eslint-disable-next-line */ }, [jobId, previewing, user?.role]);
  const reload = useLiveRefresh("jobs", () => load(true));

  // Opening a stage from a link or the timeline brings it into view.
  const [scrollTo, setScrollTo] = useState(initialStageKey);
  useEffect(() => {
    if (loading || !scrollTo) return;
    cards.current[scrollTo]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setScrollTo(null);
  }, [loading, scrollTo]);

  // Wait for what loads by itself (the Stores sheet's figures, the QR images) before opening the print dialog.
  useEffect(() => {
    if (!printAll) return undefined;
    let tries = 0, last = -1, steady = 0;
    const timer = setInterval(() => {
      const box = allRef.current;
      if (!box) return;
      const qrs = box.querySelectorAll('[data-testid="stage-qr"]').length;
      steady = qrs === last ? steady + 1 : 0;
      last = qrs;
      const loading = /Loading the stores sheet/.test(box.textContent || "");
      if (++tries > 40 || (!loading && qrs > 0 && steady >= 3)) {
        clearInterval(timer);
        const nodes = [];
        box.querySelectorAll("[data-printall]").forEach((w) => {
          const pages = w.querySelectorAll('[data-testid^="slitting-page-"], [data-testid^="quality-page-"]');
          if (pages.length) pages.forEach((n) => nodes.push(n)); else if (w.firstElementChild) nodes.push(w.firstElementChild);
        });
        printElement(nodes);
        setPrintAll(false);
      }
    }, 200);
    return () => clearInterval(timer);
  }, [printAll]);

  const openStage = (key) => {
    const stage = stages.find((s) => s.key === key);
    if (!stage) return;
    if (!stage.locked) setExpanded(key);
    setScrollTo(key);
  };

  if (loading) return <div className="p-6 text-sm text-neutral-500">Loading production flow…</div>;

  return (
    <div className="space-y-3" data-testid="production-flow">
      <LotsBanner jobId={jobId} version={job?.updated_at} />
      {job && <MilestonesTimeline job={job} stages={stages} onSelectStage={openStage} jobDone={jobDone} />}
      {stages.map((s, idx) => (
        <div key={s.key} ref={(el) => { cards.current[s.key] = el; }} className="scroll-mt-4">
          <StageCard
            stage={s}
            index={idx}
            expanded={expanded === s.key}
            onToggle={() => setExpanded(expanded === s.key ? null : s.key)}
            onSaved={reload}
            jobId={jobId}
            jobDone={jobDone}
          />
        </div>
      ))}
      {stages.length > 1 && (
        <div className="flex items-center justify-end gap-3 pt-1" data-testid="print-all-bar">
          <span className="text-xs text-neutral-500">Every sheet of this job, one per page, with its QR last.</span>
          <Button type="button" variant="outline" className="rounded-sm gap-1.5" disabled={printAll} onClick={() => setPrintAll(true)} data-testid="print-all-sheets">
            <Printer size={14} /> {printAll ? "Preparing the sheets…" : "Print all sheets"}
          </Button>
        </div>
      )}
      {printAll && <AllSheets stages={stages} job={job} jobId={jobId} innerRef={allRef} />}
    </div>
  );
}

const NO_CHANGE = () => {};
// The sheets of a job, in paper order, as printed by "Print all sheets": each stage as saved (a stage not started
// prints blank), read only, then a last page with the job's QR for all its data. Flatbed is left out (only some jobs
// use it) and the COA is its own certificate (PDF).
function AllSheets({ stages, job, jobId, innerRef }) {
  const order = ["stores_issue", "printing", "avt", "slitting", "quality", "packing", "dispatch"];
  const have = order.map((k) => stages.find((s) => s.key === k)).filter(Boolean);
  return (
    <div className="hidden" ref={innerRef} data-testid="all-sheets">
      {have.map((s) => {
        const ctx = s.context || {};
        const data = s.data || {};
        const sheet = {
          stores_issue: <StoresStageSheet bpr={ctx.common?.mpwo_number} data={data} set={NO_CHANGE} readOnly />,
          printing: <PrintingSheet ctx={ctx} data={data} setMany={NO_CHANGE} readOnly />,
          avt: <AvtSheet ctx={ctx} data={data} setMany={NO_CHANGE} readOnly />,
          slitting: <SlittingSheet ctx={ctx} data={data} setMany={NO_CHANGE} readOnly />,
          quality: <QualitySheet ctx={ctx} data={data} setMany={NO_CHANGE} readOnly />,
          packing: <PackingSheet ctx={ctx} data={data} setMany={NO_CHANGE} readOnly />,
          dispatch: <StagePrintSheet stage={s} data={data} ctx={ctx} />,
        }[s.key];
        return <div key={s.key} data-printall={s.key}>{sheet}</div>;
      })}
      <div data-printall="qr">
        <div className="bg-white text-neutral-900 border border-neutral-700 px-6 py-8 text-center" data-testid="all-sheets-qr-page">
          <div className="font-bold text-sm">MedPack India Pvt. Ltd.</div>
          <div className="text-xs text-neutral-600 mt-1">Scan for all the data of this job</div>
          <div className="my-6 flex justify-center"><StageQr jobId={jobId} stage="" size={60} /></div>
          <div className="font-mono-num text-2xl font-bold">{job?.mpwo_number || job?.job_number}</div>
          <div className="font-mono-num text-lg font-semibold mt-1">{job?.product_code}</div>
          <div className="text-sm mt-2">{[job?.customer_name, job?.product_name].filter(Boolean).join(" · ")}</div>
          {job?.quantity != null && <div className="text-sm font-semibold mt-1">Quantity {Number(job.quantity).toLocaleString("en-IN")} {job.unit || ""}</div>}
        </div>
      </div>
    </div>
  );
}

// Follow-up for a partly printed job: the original job and its lots (each followed and dispatched on its own).
function LotsBanner({ jobId, version }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get(`/jobs/${jobId}/lots`).then(({ data }) => setInfo(data)).catch(() => setInfo(null));
  }, [jobId, version]);
  if (!info?.has_lots) return null;
  const n = (v) => Number(v || 0).toLocaleString("en-IN");
  const row = (j, label) => (
    <Link key={j.id} to={`/orders/${j.id}`} className={`flex items-center justify-between gap-3 px-3 py-1.5 text-xs hover:bg-amber-100 ${j.id === info.this_id ? "bg-amber-100 font-semibold" : ""}`}>
      <span className="font-mono-num">{j.mpwo_number}{j.id === info.this_id ? " (this job)" : ""}</span>
      <span className="text-neutral-600">{label}</span>
      <span className="font-mono-num">{n(j.quantity)}{j.rolls ? ` · ${n(j.rolls)} rolls` : ""}</span>
      <span className="uppercase tracking-wider text-[10px] font-bold text-amber-900 w-36 text-right">{j.status}</span>
    </Link>
  );
  return (
    <div className="border border-amber-300 bg-amber-50 rounded-sm" data-testid="lots-banner">
      <div className="px-3 py-2 text-xs text-amber-900 border-b border-amber-200">
        <b>Split job.</b> {n(info.original_quantity)} labels ordered; the finished part was passed on as lots and each lot is followed,
        packed and dispatched on its own. The original job carries the balance still in process.
      </div>
      <div className="divide-y divide-amber-200">
        {info.lots.map((l) => row(l, `Lot ${l.lot_no} · ${l.split_stage ? `done at ${l.split_stage}` : "printed part"}`))}
        {row(info.parent, "Balance still in process")}
      </div>
      {(info.activity || []).length > 0 && (
        <details className="border-t border-amber-200" data-testid="lots-activity">
          <summary className="px-3 py-1.5 text-xs cursor-pointer text-amber-900 font-semibold">Activity ({info.activity.length})</summary>
          <div className="px-3 pb-2 max-h-56 overflow-y-auto divide-y divide-amber-100">
            {info.activity.map((a, i) => (
              <div key={i} className="py-1 text-[11px] flex gap-2">
                <span className="text-neutral-500 whitespace-nowrap font-mono-num">{new Date(a.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                <span className="font-mono-num text-amber-900 whitespace-nowrap">{a.job}</span>
                <span className="flex-1">{a.text}</span>
                <span className="text-neutral-500 whitespace-nowrap">{a.by}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function StageCard({ stage, index, expanded, onToggle, onSaved, jobId, jobDone }) {
  const Icon = STAGE_ICONS[stage.key] || CheckCircle2;
  const done = !!stage.completed_at;
  // A dispatched/cancelled job's unfilled stages aren't "stuck" — they were bulk-imported with only a
  // final status, never walked stage-by-stage. Shown plainly instead of a fake completion or an alarming
  // "Pending"/"Locked" on a job that's actually finished.
  const skippedOnDoneJob = jobDone && !done;
  return (
    <div
      className={`border rounded-sm bg-white transition-all ${
        done
          ? "border-emerald-300"
          : skippedOnDoneJob
            ? "border-neutral-200"
            : stage.locked
              ? "border-neutral-200 opacity-60"
              : "border-[#FF5A00] shadow-[0_0_0_1px_rgba(255,90,0,0.08)]"
      }`}
      data-testid={`stage-${stage.key}`}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={stage.locked}
        data-testid={`stage-${stage.key}-header`}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
          stage.locked ? "cursor-not-allowed opacity-60" : "hover:bg-neutral-50"
        }`}
      >
        <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-white text-xs font-bold relative ${
          done
            ? "bg-emerald-500"
            : stage.locked
              ? "bg-neutral-300"
              : skippedOnDoneJob
                ? "bg-neutral-400"
                : "bg-[#FF5A00] shadow-[0_0_0_2px_rgba(255,90,0,0.12)] before:absolute before:inset-0 before:rounded-full before:border before:border-[#FF5A00]/60 before:animate-ping"
        }`}>
          {done ? <CheckCircle2 size={16} /> : stage.locked ? <Lock size={13} /> : index + 1}
        </span>
        <Icon size={16} className="text-neutral-500" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-neutral-800">{stage.label}</div>
          <div className="text-[11px] text-neutral-500 mt-0.5">
            {done ? `Completed ${new Date(stage.completed_at).toLocaleString()} by ${stage.completed_by || "—"}` :
             skippedOnDoneJob ? "Not recorded — job already dispatched" :
             stage.locked ? "Locked — complete previous stage" :
             stage.optional ? "Optional — fill only if this job needs it" :
             "Pending"}
          </div>
        </div>
        {done && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">DONE</span>}
        <WasteChip stageKey={stage.key} data={stage.data} />
      </button>

      {expanded && !stage.locked && (
        <StageForm stage={stage} onSaved={onSaved} jobId={jobId} />
      )}
    </div>
  );
}

// What the stages before this one recorded — a few figures, the remark, who completed it and when — so a
// scan of a stage's sheet shows what this stage was handed. Read only.
function EarlierStages({ earlier }) {
  if (!earlier?.length) return null;
  const show = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v);
  return (
    <div className="border border-neutral-200 rounded-sm divide-y divide-neutral-100 bg-neutral-50/60" data-testid="earlier-stages">
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">From earlier stages · read only</div>
      {earlier.map((e) => (
        <div key={e.key} className="px-3 py-2 text-xs" data-testid={`earlier-${e.key}`}>
          <div className="flex flex-wrap items-center gap-x-2">
            <span className="font-semibold text-neutral-800">{e.label}</span>
            <span className={e.completed_at ? "text-emerald-700" : "text-amber-700"}>
              {e.completed_at
                ? `Completed ${new Date(e.completed_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}${e.completed_by ? ` by ${e.completed_by}` : ""}`
                : "In progress"}
            </span>
          </div>
          {e.items.length > 0 && (
            <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-0.5">
              {e.items.map((i) => (
                <div key={i.label}><span className="text-neutral-500">{i.label}: </span><span className="font-medium font-mono-num text-neutral-800">{show(i.value)}</span></div>
              ))}
            </div>
          )}
          {e.remarks && <div className="mt-1 text-neutral-700"><span className="text-neutral-500">Remarks: </span>{e.remarks}</div>}
        </div>
      ))}
    </div>
  );
}

function StageForm({ stage, onSaved, jobId }) {
  const { user } = useAuth();
  const ctx = stage.context || {};
  // Once a department saves a stage complete, it's their signed-off paper record — only admin can
  // still edit it (a correction, not routine re-editing). Mirrors the backend's own save_stage check.
  const locked = !!stage.completed_at && !isAdmin(user);

  // Auto-seed AVT + Dispatch fields from the previous stage's numbers so
  // operators don't re-key what the system already knows. Only touches empty
  // fields so an explicit override is never overwritten.
  const seeded = (d) => {
    const next = { ...d };
    const carried = ctx.carried || {};
    const common = ctx.common || {};
    const blank = (v) => v === undefined || v === "" || v === null;
    if (stage.key === "avt" && blank(next.qty_received_from_printing) && carried.qty_forward_to_avt_labels != null) {
      next.qty_received_from_printing = carried.qty_forward_to_avt_labels;
    }
    if (stage.key === "dispatch" && blank(next.receive_qty) && carried.finished_qty != null) {
      next.receive_qty = carried.finished_qty;
    }
    // Slitting: the labels AVT forwarded are the first row's "Issued Qty. from AVT".
    if (stage.key === "slitting" && blank(next.qty_received_from_avt) && !(next.slit_rows || []).length && carried.qty_forward_to_slitting != null) {
      next.qty_received_from_avt = carried.qty_forward_to_slitting;
    }
    // Printing: the metres stores issued are the first run's "Issued Qty from Stores (A)".
    if (stage.key === "printing" && blank(next.issued_a_meters) && !(next.run_rows || []).length && carried.issued_qty_meters != null) {
      next.issued_a_meters = carried.issued_qty_meters;
    }
    // Printing: what Planning already fixed on the job card (ups, die, direction) is typed once there,
    // not re-typed on the floor — only ever fills a blank field, never overrides what the operator entered.
    if (stage.key === "printing") {
      if (blank(next.ups_across) && common.ups_across != null) next.ups_across = common.ups_across;
      if (blank(next.ups_around) && common.ups_along != null) next.ups_around = common.ups_along;
      const upsTotal = common.total_ups_per_repeat ?? (common.ups_across > 0 && common.ups_along > 0 ? common.ups_across * common.ups_along : null);
      if (blank(next.no_of_ups) && upsTotal != null) next.no_of_ups = upsTotal;
      if (blank(next.directions) && common.direction) next.directions = common.direction;
      if (blank(next.die_no) && common.die_code) next.die_no = common.die_code;
      if (blank(next.label_size) && common.die_size) next.label_size = common.die_size;
      if (blank(next.equipment_id) && common.assigned_machine_code) next.equipment_id = common.assigned_machine_code;
      // Repeat Length is the cylinder's own circumference — the sheet's "Quantity forwarded" (and so
      // "No. of Printed Rolls") formula needs it; without it both silently stay blank however much of
      // the reconciliation table gets filled in.
      if (blank(next.repeat_length_mm) && common.cylinder_mm != null) next.repeat_length_mm = common.cylinder_mm;
    }
    // Packing: the repeat's ups and the roll width are the job card's, same as every other stage.
    if (stage.key === "packing") {
      const upsTotal = common.total_ups_per_repeat ?? (common.ups_across > 0 && common.ups_along > 0 ? common.ups_across * common.ups_along : null);
      if (blank(next.ups) && upsTotal != null) next.ups = upsTotal;
      if (blank(next.width_mm) && common.paper_width_mm != null) next.width_mm = common.paper_width_mm;
    }
    return next;
  };

  const [data, setData] = useState(() => seeded(stage.data || {}));
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [newerSaved, setNewerSaved] = useState(null); // who saved this stage while it was being edited here
  const formRef = useRef(null);
  const printRef = useRef(null);
  // The Stores stage prints as the paper form; every other stage as its record (what is on screen, saved or not).
  const sheetId = { stores_issue: "stores-sheet", printing: "printing-sheet", avt: "avt-sheet", slitting: "slitting-sheet", quality: "quality-sheet", packing: "packing-sheet" }[stage.key];
  // Slitting and Quality are two paper pages each: both print, one per sheet.
  // The COA is the certificate itself (QC008/F01-00): Print opens its PDF.
  const openCoaPdf = async () => {
    try {
      const res = await fetch(`${API_BASE}/jobs/${jobId}/coa/pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch (err) { toast.error(`PDF error: ${err.message}`); }
  };
  const print = () => stage.key === "coa" ? openCoaPdf() : printElement(["slitting", "quality"].includes(stage.key) ? formRef.current.querySelectorAll(`[data-testid^="${stage.key}-page-"]`)
    : sheetId ? formRef.current.querySelector(`[data-testid="${sheetId}"]`) : printRef.current);

  useEffect(() => {
    setData((d) => seeded(d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage.key, JSON.stringify(ctx.carried || {})]);

  // The stage was saved elsewhere (another user, another tab): take it over if nothing
  // has been typed here, otherwise say so and let the user choose.
  const incoming = JSON.stringify(stage.data || {});
  const lastSeen = useRef(incoming);
  useEffect(() => {
    if (incoming === lastSeen.current) return;
    lastSeen.current = incoming;
    if (touched) setNewerSaved(stage.updated_by || "another user");
    else setData(seeded(stage.data || {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming]);
  const takeSaved = () => { setData(seeded(stage.data || {})); setTouched(false); setNewerSaved(null); };

  const upd = (k, v) => {
    setTouched(true);
    setData((d) => recompute(d, k, v));
  };
  const updMany = (patch) => {
    setTouched(true);
    setData((d) => ({ ...d, ...patch }));
  };
  const recompute = (d, k, v) => {
    const next = { ...d, [k]: v };
    const numOrZero = (x) => { const n = parseFloat(x); return Number.isFinite(n) ? n : 0; };
    // Auto-compute reconciliation numbers for the Printing stage
    if (["make_ready_wastage_b", "total_counter_c", "partial_counter_d", "issued_a_meters"].includes(k)) {
      const A = numOrZero(next.issued_a_meters);
      const B = numOrZero(next.make_ready_wastage_b);
      const C = numOrZero(next.total_counter_c);
      const D = numOrZero(next.partial_counter_d);
      next.in_process_wastage_e = C - D;
      next.total_consumption_f = B + C;
      next.qty_returned_to_stores = A - (B + C);
    }
    // AVT: wastage = received (from printing) − forwarded to slitting
    if (stage.key === "avt" && ["qty_received_from_printing", "qty_forward_to_slitting"].includes(k)) {
      const rec = numOrZero(next.qty_received_from_printing);
      const fwd = numOrZero(next.qty_forward_to_slitting);
      next.wastage_qty = Math.max(0, rec - fwd);
    }
    return next;
  };

  // Line Clearance's "Previous Job Name" / "Previous Product Code" are about the MACHINE, not this
  // job — whatever last ran on this same equipment at this same stage, looked up fresh whenever
  // Equipment I.D. changes. Only fills while still blank/the "NA" placeholder, so a real typed-over
  // value (or a genuinely first-ever run) is never overwritten.
  const LC_STAGES = ["printing", "avt", "slitting", "packing"];
  const lcLookedUpFor = useRef(null);
  useEffect(() => {
    if (!LC_STAGES.includes(stage.key)) return;
    const eq = (data.equipment_id || "").trim();
    const blankish = (v) => !v || v === "NA";
    if (!eq || eq === lcLookedUpFor.current) return;
    if (!blankish(data.previous_job_name) && !blankish(data.previous_product_code)) { lcLookedUpFor.current = eq; return; }
    const t = setTimeout(() => {
      lcLookedUpFor.current = eq;
      api.get("/jobs/line-clearance/previous", { params: { stage: stage.key, equipment_id: eq, exclude_job_id: jobId } })
        .then(({ data: res }) => {
          if (!res.found) return;
          setData((d) => ({
            ...d,
            ...(blankish(d.previous_job_name) && { previous_job_name: res.previous_job_name }),
            ...(blankish(d.previous_product_code) && { previous_product_code: res.previous_product_code }),
          }));
        })
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [data.equipment_id, stage.key, jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Printing: labels printed so far against the order. Less than the order saved as a draft is a partly printed job.
  const printedLabels = stage.key === "printing" ? (parseFloat(data.qty_forward_to_avt_labels) || 0) : 0;
  const orderedLabels = parseFloat(ctx.common?.quantity) || 0;

  // Split: pass the part that is done at this stage (say 5 of 10 rolls) to the next department as its own lot;
  // the balance stays here at the same stage.
  const canSplit = ["printing", "avt", "slitting", "quality", "packing"].includes(stage.key) && !stage.completed_at && orderedLabels > 0;
  const perRoll = parseFloat(ctx.common?.labels_per_roll) || 0;
  const [split, setSplit] = useState(null); // { rolls, qty } while the dialog is open
  const splitWord = stage.key === "printing" ? "Printed" : "Done";
  const openSplit = () => setSplit({ rolls: "", qty: stage.key === "printing" && printedLabels > 0 ? String(printedLabels) : "" });
  const changeRolls = (v) => setSplit((sp) => ({ ...sp, rolls: v, qty: perRoll > 0 && parseFloat(v) > 0 ? String(Math.round(parseFloat(v) * perRoll)) : sp.qty }));
  const splitQty = parseFloat(split?.qty) || 0;
  const doSplit = async () => {
    if (!(splitQty > 0)) { toast.error("Enter the labels (or the rolls) that are done."); return; }
    if (splitQty >= orderedLabels) { toast.info("That is the whole quantity: use Save & Complete Stage."); return; }
    setSaving(true);
    try {
      // the stage as it is on screen goes with the lot, so save it first
      const saved = buildPayload();
      await api.post(`/jobs/${jobId}/stages/${stage.key}`, { data: saved, complete: false });
      const { data: res } = await api.post(`/jobs/${jobId}/lots`, { stage: stage.key, qty: splitQty, rolls: parseFloat(split.rolls) || null });
      toast.success(`${res.lot.mpwo_number} is on its way to the next stage. ${res.remaining.toLocaleString("en-IN")} labels left on this job.`);
      setSplit(null);
      setTouched(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // The stage as saved: each sheet's totals (read by the waste alerts and the later stages) follow its rows.
  const buildPayload = () => (stage.key === "printing" ? { ...data, ...totalsPatch(data) }
    : stage.key === "avt" ? { ...data, ...avtTotalsPatch(data, ctx.carried) }
    : stage.key === "slitting" ? { ...data, ...slitTotalsPatch(data) }
    : stage.key === "quality" ? { ...data, ...qualityTotalsPatch(data) }
    : stage.key === "packing" ? { ...data, ...packTotalsPatch(data) } : data);

  const save = async (markComplete) => {
    if (markComplete && stage.key === "printing" && orderedLabels > 0 && printedLabels < orderedLabels
      && !window.confirm(`Only ${printedLabels.toLocaleString("en-IN")} of ${orderedLabels.toLocaleString("en-IN")} labels are printed. Complete the Printing stage anyway? (Use Save Draft to keep the job as partially printed and print the rest later.)`)) return;
    // Every other stage's "Save & Complete" gets one plain confirmation — it locks the stage (no more
    // edits without an admin) and hands the job to the next department. Printing and Dispatch already
    // ask their own, more specific question above/below, so they're not asked this one too.
    if (markComplete && !["printing", "dispatch"].includes(stage.key)
      && !window.confirm(`Complete the ${stage.label} stage? This locks it and moves the job on to the next department.`)) return;
    let closeShort = false;
    if (markComplete && stage.key === "dispatch") {
      const sent = parseFloat(data.dispatch_qty) || 0;
      // what there is to send: what Packing handed on (the receive quantity), not the order quantity
      const held = parseFloat(data.receive_qty) || parseFloat(ctx.carried?.finished_qty) || orderedLabels;
      if (sent > 0 && held > 0 && sent < held * 0.98) {
        const left = Math.round(held - sent).toLocaleString("en-IN");
        const nf = (n) => Math.round(n).toLocaleString("en-IN");
        if (window.confirm(`Dispatching ${nf(sent)} of the ${nf(held)} labels packed.\n\nOK: send this part now and keep the other ${left} open as a part dispatch, to be sent later.\nCancel: choose again.`)) {
          closeShort = false;
        } else if (window.confirm(`Close the job at ${nf(sent)} labels instead?\n\nThe other ${left} labels will NOT be sent.`)) {
          closeShort = true;
        } else {
          return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const { data: saved } = await api.post(`/jobs/${jobId}/stages/${stage.key}`, { data: payload, complete: markComplete, close_short: closeShort });
      if (saved?.partial) {
        toast.success(`Part dispatch: ${Math.round(saved.partial.sent).toLocaleString("en-IN")} labels sent as ${saved.partial.lot}. ${Math.round(saved.partial.remaining).toLocaleString("en-IN")} still to send on this job.`);
        setTouched(false);
        setNewerSaved(null);
        onSaved();
        return;
      }
      // What's on screen is now what's saved; the refresh below brings the merged copy back.
      setTouched(false);
      setNewerSaved(null);
      // Waste is flagged for review, never a block: the save/complete above already went through
      // regardless of how high it is, so this is purely informational — worded so it never reads
      // as a failure or something that needs undoing.
      const r = computeWastePct(stage.key, data);
      const savedMsg = markComplete ? "Stage completed — next stage unlocked." : "Stage saved.";
      if (r && r.pct >= r.alert) {
        toast.warning(`${savedMsg} Waste ${r.pct.toFixed(1)}% is above the ${r.alert}% alert level for ${stage.label} — worth a look, but it hasn't stopped anything.`);
      } else if (r && r.pct >= r.warn) {
        toast.warning(`${savedMsg} Waste ${r.pct.toFixed(1)}% is above the ${r.warn}% warning level for ${stage.label}.`);
      } else {
        toast.success(savedMsg);
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const commonEntries = Object.entries(ctx.common || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  const carriedEntries = Object.entries(ctx.carried || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");

  const boolFields = stage.fields.filter(isBoolField);
  // The ship_* fields (what the core / shipper labels carry that no earlier stage recorded) are edited in the labels panel.
  const inputFields = stage.fields.filter((k) => !isBoolField(k) && !k.startsWith("ship_"));

  return (
    <div className="border-t border-neutral-200 px-4 py-3 space-y-4" data-testid={`stage-${stage.key}-form`} ref={formRef}>
      {newerSaved && (
        <div className="flex flex-wrap items-center justify-between gap-2 border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid={`stage-${stage.key}-newer`}>
          <span><b>{newerSaved}</b> saved this stage while you were editing. Saving now keeps your values.</span>
          <button type="button" onClick={takeSaved} className="font-semibold underline">Show their version</button>
        </div>
      )}

      <EarlierStages earlier={ctx.earlier} />

      {(stage.key === "coa" || stage.key === "dispatch") && (
        <div className="no-print flex items-center justify-end gap-2 text-[10px] text-neutral-500">
          <span>Scan to open this job at {stage.label}</span>
          <StageQr jobId={jobId} stage={stage.key} />
        </div>
      )}

      {(commonEntries.length > 0 || carriedEntries.length > 0) && (
        <div className="bg-orange-50 border border-orange-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-wider text-orange-700 font-semibold mb-2">
            Carried forward · read only
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs">
            {commonEntries.map(([k, v]) => (
              <div key={k}>
                <span className="text-neutral-500">{NICE[k] || humanize(k)}: </span>
                <span className="font-medium text-neutral-800">{formatCtxVal(k, v)}</span>
              </div>
            ))}
            {carriedEntries.map(([k, v]) => (
              <div key={k}>
                <span className="text-neutral-500">{NICE[k] || humanize(k)}: </span>
                <span className="font-medium text-neutral-800">{formatCtxVal(k, v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stage.key === "stores_issue" ? (
        <StoresStageSheet bpr={ctx.common?.mpwo_number} data={data} set={upd} readOnly={locked} />
      ) : stage.key === "printing" ? (
        <>
          {orderedLabels > 0 && (printedLabels > 0 || !stage.completed_at) && (
            <div className={`border px-3 py-2 text-xs ${printedLabels <= 0 ? "border-neutral-300 bg-neutral-50 text-neutral-700" : printedLabels < orderedLabels ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`} data-testid="printing-progress">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-semibold uppercase tracking-wider text-[10px]">Printed quantity</span>
                <label className="inline-flex items-center gap-1.5">
                  <input type="number" min="0" value={data.qty_forward_to_avt_labels ?? ""} disabled={locked}
                    onChange={(e) => upd("qty_forward_to_avt_labels", e.target.value)} data-testid="printing-printed-qty"
                    className="w-28 border border-neutral-300 bg-white px-2 py-1 text-right font-mono-num text-xs" />
                  <span>of <b>{orderedLabels.toLocaleString("en-IN")}</b> labels ordered</span>
                </label>
                <span className="text-neutral-500">(fills from the runs below; you can also type it)</span>
              </div>
              {printedLabels > 0 && printedLabels < orderedLabels && (
                <div className="mt-1.5">
                  <b>{(orderedLabels - printedLabels).toLocaleString("en-IN")}</b> still to print. If only part is printed, use <b>Partially Printed</b> at the bottom right: AVT and the departments after it start on the {printedLabels.toLocaleString("en-IN")} now, and the balance stays here to print later.
                </div>
              )}
              {printedLabels >= orderedLabels && <div className="mt-1.5">The full quantity is printed.</div>}
            </div>
          )}
          <PrintingSheet ctx={ctx} data={data} setMany={updMany} readOnly={locked} />
        </>
      ) : stage.key === "coa" ? (
        <>
          <COAEditor job={{ id: jobId, mpwo_number: ctx.common?.mpwo_number }} />
          <details className="border border-neutral-200 rounded-sm" data-testid="coa-more">
            <summary className="px-3 py-2 text-xs cursor-pointer text-neutral-600">More COA details (stage record, not on the certificate): references and checks</summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
              {inputFields.map((k) => (
                <FieldEditor key={k} stageKey={stage.key} k={k} value={data[k] || ""} onChange={(v) => upd(k, v)} readOnly={locked} />
              ))}
            </div>
          </details>
        </>
      ) : stage.key === "quality" ? (
        <QualitySheet ctx={ctx} data={data} setMany={updMany} readOnly={locked} />
      ) : stage.key === "packing" ? (
        <>
          <PackingSheet ctx={ctx} data={data} setMany={updMany} readOnly={locked} />
          <details className="border border-neutral-200 rounded-sm" data-testid="packing-more">
            <summary className="px-3 py-2 text-xs cursor-pointer text-neutral-600">More packing details (not on the paper form): box number, ups, width, rolls / meters / labels details, weight. The Med Pack register uses these.</summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
              {inputFields.filter((k) => !PACK_SHEET_FIELDS.has(k)).map((k) => (
                <FieldEditor key={k} stageKey={stage.key} k={k} value={data[k] || ""} onChange={(v) => upd(k, v)} readOnly={locked} />
              ))}
            </div>
          </details>
        </>
      ) : stage.key === "slitting" ? (
        <>
          <SlittingSheet ctx={ctx} data={data} setMany={updMany} readOnly={locked} />
          <details className="border border-neutral-200 rounded-sm" data-testid="slitting-more">
            <summary className="px-3 py-2 text-xs cursor-pointer text-neutral-600">More slitting details (not on the paper forms): destruction quantity, QC sign</summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
              {inputFields.filter((k) => !SLIT_SHEET_FIELDS.has(k)).map((k) => (
                <FieldEditor key={k} stageKey={stage.key} k={k} value={data[k] || ""} onChange={(v) => upd(k, v)} readOnly={locked} />
              ))}
            </div>
          </details>
        </>
      ) : stage.key === "avt" ? (
        <>
          <AvtSheet ctx={ctx} data={data} setMany={updMany} readOnly={locked} />
          <details className="border border-neutral-200 rounded-sm" data-testid="avt-more">
            <summary className="px-3 py-2 text-xs cursor-pointer text-neutral-600">More AVT details (not on the paper form)</summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
              {inputFields.filter((k) => !AVT_SHEET_FIELDS.has(k)).map((k) => (
                <FieldEditor key={k} stageKey={stage.key} k={k} value={data[k] || ""} onChange={(v) => upd(k, v)} readOnly={locked} />
              ))}
            </div>
          </details>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {inputFields.map((k) => (
            <FieldEditor key={k} stageKey={stage.key} k={k} value={data[k] || ""} onChange={(v) => upd(k, v)} readOnly={locked} />
          ))}
        </div>
      )}

      {boolFields.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="label-caps">Dispatched Goods Checklist</div>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer text-neutral-600">
              <input
                type="checkbox"
                data-testid={`stage-${stage.key}-select-all`}
                checked={boolFields.every((k) => !!data[k])}
                disabled={locked}
                onChange={(e) => updMany(Object.fromEntries(boolFields.map((k) => [k, e.target.checked])))}
                className="accent-[#FF5A00] h-4 w-4"
              />
              <span>Select all</span>
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 border border-neutral-200 rounded-sm p-3">
            {boolFields.map((k) => (
              <label key={k} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`stage-${stage.key}-${k}`}
                  checked={!!data[k]}
                  disabled={locked}
                  onChange={(e) => upd(k, e.target.checked)}
                  className="accent-[#FF5A00] h-4 w-4"
                />
                <span>{humanize(k.replace(/^chk_(core_)?/, ""))}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {stage.key === "dispatch" && <DispatchLabels jobId={jobId} data={data} setMany={updMany} readOnly={locked} />}

      {!sheetId && (
        <div ref={printRef} className="hidden">
          <StagePrintSheet stage={stage} data={data} ctx={ctx} />
        </div>
      )}

      {split && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="split-dialog" onClick={() => !saving && setSplit(null)}>
          <div className="bg-white rounded-sm border border-neutral-300 w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <div className="font-semibold text-neutral-900">{splitWord} part: pass it to the next stage</div>
              <div className="text-xs text-neutral-500 mt-1">
                {ctx.common?.mpwo_number} · {orderedLabels.toLocaleString("en-IN")} labels{perRoll > 0 ? ` (${Math.round(orderedLabels / perRoll).toLocaleString("en-IN")} rolls of ${perRoll.toLocaleString("en-IN")})` : ""}.
                The part you enter becomes its own lot ({ctx.common?.mpwo_number}/P…) and moves on; the rest stays here at {stage.label}.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs">
                <span className="label-caps block mb-1">Rolls {splitWord.toLowerCase()}</span>
                <input type="number" min="0" value={split.rolls} onChange={(e) => changeRolls(e.target.value)} data-testid="split-rolls"
                  className="w-full border border-neutral-300 px-2 py-1.5 text-sm font-mono-num text-right" />
              </label>
              <label className="text-xs">
                <span className="label-caps block mb-1">Labels {splitWord.toLowerCase()}</span>
                <input type="number" min="0" value={split.qty} onChange={(e) => setSplit({ ...split, qty: e.target.value })} data-testid="split-qty"
                  className="w-full border border-neutral-300 px-2 py-1.5 text-sm font-mono-num text-right" />
              </label>
            </div>
            {perRoll > 0 && <div className="text-[11px] text-neutral-500">Labels fill in from the rolls ({perRoll.toLocaleString("en-IN")} labels per roll); you can still change them.</div>}
            {splitQty > 0 && splitQty < orderedLabels && (
              <div className="text-xs bg-amber-50 border border-amber-200 px-3 py-2">
                <b>{splitQty.toLocaleString("en-IN")}</b> labels go to the next stage as a new lot; <b>{(orderedLabels - splitQty).toLocaleString("en-IN")}</b> stay on {ctx.common?.mpwo_number}.
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setSplit(null)} disabled={saving}>Cancel</Button>
              <Button size="sm" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] text-white" onClick={doSplit} disabled={saving || !(splitQty > 0)} data-testid="split-confirm">
                Split and pass on
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={print} className="rounded-sm mr-auto gap-1.5" data-testid={`stage-${stage.key}-print`}>
          <Printer size={13} /> Print
        </Button>
        {canSplit && stage.key === "printing" && (
          <Button size="sm" variant="outline" onClick={openSplit} disabled={saving} data-testid="printing-pass-on"
            title="Part of the order is printed: send it to the next department now and keep the balance here"
            className="rounded-sm border-amber-500 text-amber-800 hover:bg-amber-50 gap-1.5">
            <ArrowRightCircle size={13} /> Partially Printed
          </Button>
        )}
        {canSplit && (
          <Button size="sm" variant="outline" onClick={openSplit} disabled={saving} data-testid="split-job"
            title="Split by rolls: the rolls that are done become their own job and go to the next stage; the rest stays here"
            className="rounded-sm border-amber-500 text-amber-800 hover:bg-amber-50 gap-1.5">
            <Layers size={13} /> Split Job (by rolls)
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => save(false)}
          disabled={saving}
          data-testid={`stage-${stage.key}-save`}
          className="rounded-sm"
        >
          Save Draft
        </Button>
        <Button
          size="sm"
          onClick={() => save(true)}
          disabled={saving}
          data-testid={`stage-${stage.key}-complete`}
          className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] text-white"
        >
          <Award size={13} className="mr-1.5" />
          Save & Complete Stage
        </Button>
      </div>
    </div>
  );
}

// What a stage prints (everything but Stores, which prints as the paper form): the job, the
// stage's fields as filled in, and lines to sign.
function StagePrintSheet({ stage, data, ctx }) {
  const job = Object.entries(ctx.common || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  const filled = stage.fields
    .filter((k) => data[k] !== undefined && data[k] !== null && data[k] !== "" && data[k] !== false && typeof data[k] !== "object")
    .map((k) => [NICE[k] || humanize(k), data[k] === true ? "Yes" : String(data[k])]);
  return (
    <div className="bg-white text-neutral-900 border border-neutral-700 text-xs" data-testid={`stage-${stage.key}-printout`}>
      <div className="flex justify-between px-4 py-2 border-b border-neutral-700 font-semibold">
        <span>MedPack · {stage.label}</span>
        <span className="flex items-start gap-2"><StageQr jobId={ctx.job_id} stage={stage.key} />Printed {new Date().toLocaleDateString("en-IN")}</span>
      </div>
      <div className="px-4 py-2 border-b border-neutral-700 grid grid-cols-2 gap-x-6 gap-y-1">
        {job.map(([k, v]) => (
          <div key={k}><span className="text-neutral-600">{NICE[k] || humanize(k)}: </span><b>{formatCtxVal(k, v)}</b></div>
        ))}
      </div>
      <table className="w-full border-collapse">
        <tbody>
          {filled.length === 0 && <tr><td className="px-4 py-3 text-neutral-500">Nothing filled in yet.</td></tr>}
          {filled.map(([label, value]) => (
            <tr key={label}>
              <td className="border border-neutral-700 px-3 py-1.5 w-1/3 text-neutral-700">{label}</td>
              <td className="border border-neutral-700 px-3 py-1.5 font-medium">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-4 py-2 border-t border-neutral-700 text-neutral-700">
        {stage.completed_at
          ? `Completed ${new Date(stage.completed_at).toLocaleString("en-IN")} by ${stage.completed_by || "—"}`
          : "Not completed yet"}
      </div>
      <div className="grid grid-cols-2 gap-8 px-4 pt-10 pb-3 border-t border-neutral-700">
        <div className="border-t border-neutral-700 pt-1">Done by</div>
        <div className="border-t border-neutral-700 pt-1">Checked by</div>
      </div>
    </div>
  );
}

// The Stores Issue stage is the paper form "2. Stores · Issuing of Material" (StoresSheet.jsx).
// Its quantities follow what stores issues and returns, live; the rest is this stage's record.
function StoresStageSheet({ bpr, data, set, readOnly }) {
  const [ctx, setCtx] = useState(null);
  const load = () => bpr && api.get(`/stores/bpr/${encodeURIComponent(bpr)}`)
    .then(({ data: d }) => setCtx(d))
    .catch(() => setCtx(null));
  useEffect(() => { load(); }, [bpr]); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("stores", load);
  if (!bpr || !ctx) return <div className="label-caps py-4">Loading the stores sheet…</div>;
  return <SheetBody ctx={ctx} data={data} set={set} readOnly={readOnly} />;
}
function FieldEditor({ stageKey, k, value, onChange, readOnly = false }) {
  const label = humanize(k);
  // Stage-scoped testId prevents id collisions when multiple stage forms are open
  const testId = `stage-${stageKey}-field-${k}`;
  if (COMPUTED.has(k)) {
    return (
      <div>
        <label className="label-caps block mb-1">{label}</label>
        <div
          data-testid={testId}
          className="h-9 px-3 flex items-center rounded-sm bg-neutral-100 border border-neutral-200 text-sm font-mono-num text-neutral-700"
        >
          {value === "" || value === undefined ? "—" : value}
        </div>
      </div>
    );
  }
  if (AREA_FIELDS.has(k)) {
    return (
      <div className="md:col-span-2">
        <label className="label-caps block mb-1">{label}</label>
        <textarea
          data-testid={testId}
          value={value}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className="w-full rounded-sm border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5A00] disabled:bg-neutral-100 disabled:text-neutral-500"
        />
      </div>
    );
  }
  if (DATE_FIELDS.has(k)) {
    return (
      <div>
        <label className="label-caps block mb-1">{label}</label>
        <DatePicker value={value} onChange={onChange} testId={testId} disabled={readOnly} />
      </div>
    );
  }
  if (TIME_FIELDS.has(k)) {
    return (
      <div>
        <label className="label-caps block mb-1">{label}</label>
        <TimePicker value={value} onChange={onChange} testId={testId} disabled={readOnly} />
      </div>
    );
  }
  return (
    <div>
      <label className="label-caps block mb-1">{label}</label>
      <Input
        type="text"
        data-testid={testId}
        value={value}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-sm h-9 text-sm"
      />
    </div>
  );
}

function formatCtxVal(k, v) {
  if (k === "lo_li_ticks" && v && typeof v === "object") {
    const on = Object.entries(v).filter(([, x]) => x).map(([kk]) => kk).join(", ");
    return on || "—";
  }
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
}
